import React, { useEffect, useMemo, useState } from 'react';
import type { CommunitySection, CommunityTopic } from './types';
import { formatForumDate, topicMetaLine } from './communityUtils';
import { AuthorBadge } from './CommunityShared';
import { useCommunityForum } from './CommunityForumContext';
import { PRESET_TAGS } from './types';
import { CommunityDevAdminGate, CommunityDevEntity, CommunityDevPath } from './CommunityDevUi';
import { entityRef, P } from './communityDevMode';

type Props = {
  section: CommunitySection;
  onOpenTopic: (topicId: string) => void;
  onCreateTopic: () => void;
  realIsAdmin: boolean;
};

function TopicCard({ topic, onClick }: { topic: CommunityTopic; onClick: () => void }) {
  return (
    <CommunityDevEntity storagePath={entityRef(P.topics, topic.id)}>
      <button type="button" className="dcf-topic-card" onClick={onClick}>
        <div className="dcf-topic-card__badges">
          {topic.status === 'pinned' && <span className="dcf-badge dcf-badge--pinned">Закреплено</span>}
          {topic.hasSummary && <span className="dcf-badge dcf-badge--summary">Есть сводка</span>}
          {topic.status === 'closed' && <span className="dcf-badge dcf-badge--closed">Закрыта</span>}
        </div>
        <h3 className="dcf-topic-card__title">
          {topic.title}
          <CommunityDevPath path={P.topicTitle} inline />
        </h3>
        <p className="dcf-topic-card__excerpt">
          {topic.description}
          <CommunityDevPath path={P.topicDescription} inline />
        </p>
        {topic.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {topic.tags.map((tag) => (
              <span key={tag} className="dcf-tag">{tag}</span>
            ))}
            <CommunityDevPath path={P.topicTags} inline />
          </div>
        )}
        <div className="dcf-topic-card__footer">
          <AuthorBadge author={topic.author} devPath={P.topicAuthor} />
          <span>{topicMetaLine(topic)} · {formatForumDate(topic.lastActivityAt)}</span>
        </div>
      </button>
    </CommunityDevEntity>
  );
}

export default function CommunityTopicListView({
  section,
  onOpenTopic,
  onCreateTopic,
  realIsAdmin,
}: Props) {
  const { getSectionTopics, isAdmin, loadForumTopics } = useCommunityForum();
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState<string | null>(null);

  useEffect(() => {
    void loadForumTopics(section.id);
  }, [section.id, loadForumTopics]);

  const topics = getSectionTopics(section.id);

  const filtered = useMemo(() => {
    let list = topics;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (t) =>
          t.title.toLowerCase().includes(q)
          || t.description.toLowerCase().includes(q)
          || t.tags.some((tag) => tag.toLowerCase().includes(q)),
      );
    }
    if (tagFilter) {
      list = list.filter((t) => t.tags.includes(tagFilter));
    }
    return list;
  }, [topics, search, tagFilter]);

  const canCreate =
    section.type !== 'information'
    && (section.createTopicsAccess === 'all' || isAdmin);

  return (
    <>
      <header className="dcf-header">
        <h1 className="dcf-header__title">
          {section.name}
          <CommunityDevPath path={P.forumName} inline />
        </h1>
        <p className="dcf-header__desc">
          {section.description}
          <CommunityDevPath path={P.forumDescription} inline />
        </p>
        <CommunityDevPath path={entityRef(P.forums, section.id)} />
      </header>

      <div className="dcf-toolbar">
        {canCreate && (
          <button type="button" className="dcf-btn dcf-btn--primary" onClick={onCreateTopic}>
            <i className="fas fa-plus" aria-hidden="true" />
            Создать тему
          </button>
        )}
        {!canCreate && section.type !== 'information' && section.createTopicsAccess !== 'all' && (
          <CommunityDevAdminGate
            realIsAdmin={realIsAdmin}
            isAdmin={isAdmin}
            label="Создать тему"
            storagePath={P.forumCreateTopicsAccess}
            className="inline-flex"
          >
            <button type="button" className="dcf-btn dcf-btn--primary" onClick={onCreateTopic}>
              <i className="fas fa-plus" aria-hidden="true" />
              Создать тему
            </button>
          </CommunityDevAdminGate>
        )}
        <input
          className="dcf-input dcf-search"
          placeholder="Поиск по темам"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button
          type="button"
          className={`dcf-tag dcf-tag--filter ${!tagFilter ? 'dcf-tag--filter-active' : ''}`}
          onClick={() => setTagFilter(null)}
        >
          Все теги
        </button>
        {PRESET_TAGS.slice(0, 10).map((tag) => (
          <button
            key={tag}
            type="button"
            className={`dcf-tag dcf-tag--filter ${tagFilter === tag ? 'dcf-tag--filter-active' : ''}`}
            onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      {section.type === 'information' && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Информационный раздел — только для чтения. Новые темы создаёт администратор.
          <CommunityDevPath path={`${P.forumType} = information`} inline />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="dcf-empty">
          <p className="dcf-empty__title">В этом разделе пока нет тем</p>
          <p className="dcf-empty__text">
            {search || tagFilter
              ? 'Попробуйте изменить фильтры или поисковый запрос.'
              : 'Станьте первым, кто задаст вопрос участникам рынка.'}
          </p>
          {canCreate && !search && !tagFilter && (
            <div className="dcf-empty__action">
              <button type="button" className="dcf-btn dcf-btn--primary" onClick={onCreateTopic}>
                Создать первую тему
              </button>
            </div>
          )}
        </div>
      ) : (
        filtered.map((topic) => (
          <TopicCard key={topic.id} topic={topic} onClick={() => onOpenTopic(topic.id)} />
        ))
      )}
    </>
  );
}
